import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, ShieldAlert } from 'lucide-react';

// Allow only digits (Latin 0-9 and Arabic-Indic ٠-٩); normalize Arabic digits
// to Latin so the backend gets a clean numeric string.
const ARABIC_INDIC = /[\u0660-\u0669\u06F0-\u06F9]/g;
const arabicToLatin = (s) => s.replace(ARABIC_INDIC, (d) => {
  const code = d.charCodeAt(0);
  return String((code >= 0x06F0 ? code - 0x06F0 : code - 0x0660));
});
const sanitizeNumeric = (raw) => arabicToLatin(String(raw || '')).replace(/\D/g, '').slice(0, 12);

const LoginPage = () => {
  const [readerNumber, setReaderNumber] = useState(() => sanitizeNumeric(localStorage.getItem('last_reader_number') || ''));
  const [loading, setLoading] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(0); // epoch ms; 0 if not locked
  const [nowTs, setNowTs] = useState(Date.now());
  const { login } = useAuth();
  const navigate = useNavigate();

  const remaining = Math.max(0, Math.ceil((lockedUntil - nowTs) / 1000));

  useEffect(() => {
    if (!lockedUntil) return;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (remaining > 0) return;

    if (!readerNumber) {
      toast.error('يرجى إدخال الرقم القرائي');
      return;
    }

    setLoading(true);
    const result = await login(readerNumber);
    setLoading(false);

    if (result.success) {
      localStorage.setItem('last_reader_number', readerNumber);
      toast.success('مرحباً بك!');
      navigate('/profile');
    } else if (result.retryAfter) {
      setLockedUntil(Date.now() + result.retryAfter * 1000);
      toast.error(result.message);
    } else {
      toast.error(result.message);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundImage: `linear-gradient(rgba(193, 154, 107, 0.95), rgba(93, 64, 55, 0.95)), url('https://images.unsplash.com/photo-1739994885957-add0b729f051?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="login-page"
    >
      {/* Islamic Pattern Overlay */}
      <div className="absolute inset-0 noise-bg pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_anwar-reading/artifacts/9ouhjo98_%D8%A7%D9%84%D8%B4%D8%B9%D8%A7%D8%B1%20%D9%81%D9%82%D8%B7.svg"
            alt="شعار أنوار"
            className="h-48 w-auto mx-auto drop-shadow-2xl"
          />
          <h1 className="text-4xl font-bold text-white mt-6 mb-2 drop-shadow-lg">
            تسجيل الدخول
          </h1>
          <p className="text-white/80 text-lg drop-shadow-md">
            أدخل رقمك القرائي للدخول
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border-2 border-primary/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="readerNumber" className="text-base font-semibold text-accent">
                الرقم القرائي
              </Label>
              <Input
                id="readerNumber"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                maxLength={12}
                value={readerNumber}
                onChange={(e) => setReaderNumber(sanitizeNumeric(e.target.value))}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = (e.clipboardData || window.clipboardData).getData('text');
                  setReaderNumber(sanitizeNumeric(text));
                }}
                onKeyDown={(e) => {
                  // Block letter keys before they insert (mobile IME still handled by onChange sanitize)
                  if (e.key.length === 1 && !/[0-9\u0660-\u0669\u06F0-\u06F9]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                    e.preventDefault();
                  }
                }}
                placeholder="أدخل رقمك القرائي"
                className="mt-2 h-12 text-lg"
                disabled={loading || remaining > 0}
                dir="ltr"
                data-testid="reader-number-input"
              />
            </div>

            {remaining > 0 && (
              <div className="flex items-start gap-2 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="lockout-banner">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <span>لقد تجاوزت عدد المحاولات المسموح بها. حاول مجدداً بعد <strong>{remaining}</strong> ثانية.</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 text-lg bg-primary hover:bg-primary/90 shadow-lg"
              disabled={loading || remaining > 0}
              data-testid="login-submit-btn"
            >
              {loading ? (
                'جارٍ تسجيل الدخول...'
              ) : remaining > 0 ? (
                <>محجوب مؤقتاً · {remaining} ثانية</>
              ) : (
                <>
                  <LogIn className="w-5 h-5 ml-2" />
                  تسجيل الدخول
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground text-center">
              <strong>ملحوظة:</strong> الرقم القرائي يُخصص من قبل الإدارة
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-white/90 hover:text-white underline text-lg transition-colors"
            data-testid="back-to-home-btn"
          >
            العودة للصفحة الرئيسية
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
